import React, { useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { AaiRequest, PendingReview, QuickWin } from '../../types/aai';
import { Action, Card, Chip, Drawer, ErrorNote, FactList, Kicker, StatusChip } from '../../components/aa';
import { SkillRunBody } from './SkillRun';
import { useOpenWorkspace } from '../../hooks/useOpenWorkspace';
import { DELIVERABLE_LABEL, ROUTE_LABEL } from './routeRequest';
import { nextFor as sharedNext, stateFor } from '../partner/work';

/*
 * The employee portal's work panels.
 *
 * Every list in the portal opens its object here rather than on a page of its
 * own. Two reasons, both about the person rather than the code: they keep the
 * list they were reading, and a panel makes it obvious that finishing returns
 * them to it. Full pages are for destinations; these are objects.
 *
 * A panel finishes what it starts. Running a Quick Win, running what a project
 * delivered, and reviewing a run all happen in here: nothing opens a full page
 * mid-task, so closing once always returns the reader to the list they came
 * from. The exception is Orbit, which is its own full-screen flow by design.
 *
 * Workspace delivery detail is the one genuine hand-off left, because seeing a
 * project summary and holding Workspace membership are different permissions.
 * The panel says so instead of implying access it cannot grant.
 */

const Spinner: React.FC<{ busy: boolean }> = ({ busy }) => {
  if (!busy) return null;
  return <Loader2 size={14} className="animate-spin" />;
};

/* ── Quick Win ─────────────────────────────────────────────────────────── */

export const QuickWinDrawer: React.FC<{ item: QuickWin; onClose: () => void }> = ({
  item, onClose,
}) => {
  const [running, setRunning] = useState(false);
  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Quick Win</Kicker>}
      badge={<Chip>{item.form_label}</Chip>}
      title={item.name}
      sub={item.problem}
      footer={(
        <div className="flex flex-wrap items-center gap-3">
          {!running && (
            <Action size="lg" onClick={() => setRunning(true)} disabled={!item.ready} reason={item.setup_label}>
              Run it here
            </Action>
          )}
          {running && (
            <Action size="lg" variant="quiet" onClick={() => setRunning(false)}>Back to the detail</Action>
          )}
        </div>
      )}
    >
      {running && <SkillRunBody skillId={item.id} />}
      {!running && (
        <>
          <FactList
            facts={[
              { label: 'Scope', value: item.scope_label },
              { label: 'For', value: item.audience_label },
              { label: 'Setup', value: item.setup_label },
            ]}
          />
          <p className="m-0 mt-5 text-[13px] leading-[21.5px] text-ink-dim">
            Runs stay private to you unless you share an approved result.
          </p>
        </>
      )}
    </Drawer>
  );
};

/* ── Project / request ─────────────────────────────────────────────────── */

/* What happens next, and WHO it is waiting on.
 *
 * This said "Department head review" and stopped, so the person who raised it
 * could see that somebody was holding it and never which somebody. The server
 * already works the reviewer out fresh on every read, precisely so it follows a
 * department that changes head, and it was arriving on the row unread. */
const nextFor = (request: AaiRequest): string => {
  const step = sharedNext(request);
  if (!request.reviewer_email) return step;
  return `${step} · ${request.reviewer_email}`;
};

const ownerOf = (request: AaiRequest): string => {
  const named = request.assigned_expert_email || request.owner_email || request.decided_by;
  if (!named) return 'Not yet named';
  return named;
};

/* The facts under a project, in the order they answer "where is this".

   `route` is what Orbit recommended when the request was raised, and it is
   shown as that rather than as the project's kind: what gets delivered is
   decided later, and a Quick Win that grew into a project would otherwise read
   as a record that had been quietly rewritten. */
const factsFor = (request: AaiRequest): { label: string; value: string }[] => {
  const facts: { label: string; value: string }[] = [];
  // Named on another company's work: say whose before anything else.
  if (request.company_name) facts.push({ label: 'Client', value: request.company_name });
  facts.push({ label: 'Owner', value: ownerOf(request) });
  facts.push({ label: 'Next', value: nextFor(request) });
  facts.push({ label: 'Department', value: request.department || 'Not recorded' });
  if (request.route) facts.push({ label: 'Asked for as', value: ROUTE_LABEL[request.route] });
  // The shape, on the requester's own copy of their request. It was only ever
  // shown to the Owner, so the person who answered the questions that decided
  // it could not see what came out. Blank stays blank: nobody classified it.
  if (request.deliverable) {
    facts.push({ label: 'Built as', value: DELIVERABLE_LABEL[request.deliverable] });
  }
  return facts;
};

export const RequestDrawer: React.FC<{ request: AaiRequest; onClose: () => void }> = ({
  request, onClose,
}) => {
  const [running, setRunning] = useState(false);
  const skillId = request.tool_skill_id;
  const { opening, open } = useOpenWorkspace();
  const buildId = request.builder_project_id;
  // Two doors, never both: scoping runs until the brief turns into a project,
  // and the Planner is what that project opens in. Offering both at once would
  // ask the reader to know which half of the lifecycle they are in.
  const scoping = !!request.problem_id && !buildId;
  // Work named to you in another company carries that company; your own work
  // does not, so it falls back to the one you are signed into. Crossing on the
  // stored id alone would send a delivery expert into their OWN workspace to
  // look for a client's project.
  const workspace = request.company_id || localStorage.getItem('client_id');
  const planner = () => open(workspace, `/customer/agent/${buildId}`);
  const carryOnScoping = () => open(
    workspace,
    async () => (await aaiApi.scopeInWorkspace(request.id)).data.path,
  );
  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Project</Kicker>}
      badge={<StatusChip status={stateFor(request)} />}
      title={request.title}
      sub={request.detail}
      footer={(
        <div className="flex flex-wrap items-center gap-3">
          {!running && skillId && (
            <Action size="lg" onClick={() => setRunning(true)}>Run what was delivered</Action>
          )}
          {!running && !skillId && (
            <Action size="lg" disabled reason="Nothing has been delivered on this project yet">
              Run what was delivered
            </Action>
          )}
          {!running && buildId && (
            <Action size="lg" variant="outline" onClick={planner} disabled={opening}>
              Continue in the Planner
            </Action>
          )}
          {!running && scoping && (
            <Action size="lg" variant="outline" onClick={carryOnScoping} disabled={opening}>
              Continue scoping
            </Action>
          )}
          {running && (
            <Action size="lg" variant="quiet" onClick={() => setRunning(false)}>Back to the detail</Action>
          )}
        </div>
      )}
    >
      {running && skillId && <SkillRunBody skillId={skillId} />}
      {!running && (
        <>
          <FactList facts={factsFor(request)} />
          {buildId && (
            <p className="m-0 mt-5 text-[13px] leading-[21.5px] text-ink-dim">
              The build for this project runs in the Workspace. Continue in the Planner opens it on
              this project, and the Workspace has a way back here.
            </p>
          )}
          {scoping && (
            <p className="m-0 mt-5 text-[13px] leading-[21.5px] text-ink-dim">
              This is still being scoped. Continue scoping opens the Workspace on this exact
              request and picks up where it stopped, rather than asking everything again.
            </p>
          )}
          {!buildId && !scoping && (
            <p className="m-0 mt-5 text-[13px] leading-[21.5px] text-ink-dim">
              Nothing is being built for this project yet. Once it is, you can carry on with it in
              the Planner from here.
            </p>
          )}
        </>
      )}
    </Drawer>
  );
};

/* ── Review a run ──────────────────────────────────────────────────────── */

// The keys are the stored vocabulary (aai_run_review.outcome, constrained to
// worked / partial / failed); the labels are how a person says the same thing.
// They were once 'helped' / 'partly' / 'no_help', which the endpoint refused,
// so every review a person wrote was answered with "nothing was saved".
const OUTCOMES = [
  { key: 'worked', label: 'It helped', desc: 'The result was usable on the work I was doing.' },
  { key: 'partial', label: 'It partly helped', desc: 'Usable after I corrected or added to it.' },
  { key: 'failed', label: 'It did not help', desc: 'I did not use the result.' },
];

/*
 * The review is the work, and it is the only place a live result comes from:
 * hours only enter Results once a person confirms them here. The minutes field
 * stays optional and empty by default, because a prefilled number is a number
 * somebody did not measure.
 */
export const ReviewDrawer: React.FC<{
  review: PendingReview;
  onClose: () => void;
  onSubmitted: () => void;
}> = ({ review, onClose, onSubmitted }) => {
  const [outcome, setOutcome] = useState('');
  const [minutes, setMinutes] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const parsedMinutes = (): number | null => {
    const n = Number(minutes.trim());
    if (!minutes.trim() || Number.isNaN(n)) return null;
    return n;
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await aaiApi.reviewRun(review.execution_id, {
        outcome,
        minutes_saved: parsedMinutes(),
        note: note.trim(),
      });
      onSubmitted();
      setDone(true);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || 'Could not record that review. Nothing was saved, so you can try again.');
    } finally {
      setBusy(false);
    }
  };

  const disabledReason = { true: '', false: 'Choose what the result gave you first' };

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Results</Kicker>}
      badge={<Chip tone="warn">Needs review</Chip>}
      title={review.skill_name}
      sub="Say what this run gave you. Only a reviewed run becomes a live result, so this is the step that puts hours on the record."
      footer={(
        <div className="flex flex-wrap items-center gap-3">
          {!done && (
            <Action
              size="lg"
              onClick={submit}
              disabled={busy || !outcome}
              reason={disabledReason[String(Boolean(outcome)) as 'true' | 'false'] || 'Working…'}
            >
              <Spinner busy={busy} /> Record review
            </Action>
          )}
          {done && <Action size="lg" onClick={onClose}>Close</Action>}
        </div>
      )}
    >
      {error && <ErrorNote className="mb-4" message={error} />}

      {done && (
        <div className="flex items-start gap-3.5">
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-ok-dim text-white">
            <Check size={14} strokeWidth={2.5} />
          </span>
          <p className="m-0 text-[14px] leading-[23px] text-ink">
            Recorded. Your live results behind this panel are already updated.
          </p>
        </div>
      )}

      {!done && (
        <>
          <Kicker tone="muted">What did it give you</Kicker>
          <div className="mt-2 grid gap-2">
            {OUTCOMES.map((option) => (
              <label
                key={option.key}
                className="flex cursor-pointer items-start gap-2.5 rounded-act border border-line bg-white px-3.5 py-3 transition-colors hover:bg-canvas"
              >
                <input
                  type="radio"
                  name="outcome"
                  checked={outcome === option.key}
                  onChange={() => setOutcome(option.key)}
                  className="mt-1 shrink-0 border-line-strong text-ink focus:ring-ink/20"
                />
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold leading-[21.5px] text-ink">{option.label}</span>
                  <span className="block text-[13px] leading-[21.5px] text-ink-dim">{option.desc}</span>
                </span>
              </label>
            ))}
          </div>

          <label className="mt-5 block">
            <span className="block text-[13px] font-semibold leading-[21.5px] text-ink">
              Minutes it saved you (optional)
            </span>
            <input
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              inputMode="numeric"
              placeholder="Leave empty if you would be guessing"
              className="mt-1.5 w-full rounded-act border border-input bg-white px-3.5 py-2.5 font-sans text-[14px] tabular-nums text-ink outline-none transition-colors placeholder:font-sans placeholder:text-ink-soft focus:border-ink"
            />
          </label>

          <label className="mt-4 block">
            <span className="block text-[13px] font-semibold leading-[21.5px] text-ink">
              Anything worth noting (optional)
            </span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="mt-1.5 w-full resize-y rounded-act border border-input bg-white p-3.5 text-[14px] leading-[23px] text-ink outline-none transition-colors placeholder:text-ink-soft focus:border-ink"
              placeholder="What you had to correct, or what made it work"
            />
          </label>

          <Card className="mt-5" pad="md">
            <p className="m-0 text-[13px] leading-[21.5px] text-ink-dim">
              Your note stays with the run. The confirmed minutes are what a department rollup
              counts, and it counts them without naming you.
            </p>
          </Card>
        </>
      )}
    </Drawer>
  );
};
